// Plain-English translations of the probe harness's own fixed vocabularies --
// shared by AdminSchemaDiff.jsx and AdminDevHubCapture.jsx (item 151, 2026-09-25)
// so the two pages can never drift into two different explanations of the same
// underlying value. ONE source of truth per vocabulary, grepped from the real
// code that sets these values, not guessed:
//
//   - STAGE7_OUTCOME_PLAIN: services/probe_harness/body_loop.py's `result.outcome`
//     (the only 6 values that function ever sets).
//   - CLASSIFICATION_PLAIN: services/probe_harness/endpoint_probe.py's `_classify`
//     (the only 7 fixed values it returns, plus a dynamic `http_{status}` for an
//     unclassified status code -- handled by classificationPlain()'s fallback,
//     not hand-listed here).
//
// Scott, 2026-09-25 (Schema Diff Review): "I really don't understand what any
// of this stuff means. Is there a way you can explain it in english on the
// page?" -- then, once that page's plain-English text was confirmed good: "Do
// the same for the probe harness stuff as well."

export const STAGE7_OUTCOME_PLAIN = {
  resolved: 'Found a real set of parameters that works, proven with actual data back from the vendor. Nothing more to do here.',
  accepted_empty: 'The vendor accepted the request but sent back zero rows. That could mean this tenant genuinely has no such data, or that the value tried simply didn’t matter to the vendor — not proven either way, so this hasn’t been promoted.',
  exhausted: 'The harness tried every lead the vendor’s own refusals gave it and got stuck. Needs a human, or the vendor’s spec, to go further.',
  stuck: 'The vendor says a request body is required but never named which field it wants — no lead to follow. Needs the vendor spec or a human.',
  blocked: 'The request itself looks like it’s being discarded unread (wrong slot or content type), not a missing-parameter problem. Needs a human to check how the request is being sent.',
  no_params_required: 'The vendor returned data without needing any parameters at all. Useful to know, but since nothing was actually required, there’s no parameter contract to prove.',
}

export function stage7OutcomePlain(outcome) {
  return STAGE7_OUTCOME_PLAIN[outcome] || null
}

export const CLASSIFICATION_PLAIN = {
  works: 'Reached the endpoint and got real data back — works as-is, no body/parameter needed.',
  needs_body: 'Reached the endpoint, but the vendor wants a request body or parameter before it will answer — that’s what Stage 7 Body Discovery is for.',
  permission: 'The vendor refused this login/connection permission to reach it (401/403) — a credentials or access-grant problem, not something Stage 7 can talk its way past.',
  not_found: 'The vendor returned 404 for this endpoint — either it doesn’t exist for this product/tenant, or the path is wrong.',
  unreachable: 'The request never got a response at all (timeout, connection failure) — a network or vendor-availability problem, not a parameter problem.',
  server_error: 'The vendor’s own server errored (500+) answering this — a vendor-side problem, not something to fix by changing what we send.',
  suspect: 'Got a 200, but the body looks like an HTML login page, not real data — usually means the session/token wasn’t actually authenticated for this call.',
  skipped_write: 'This is a write (create/update/delete) endpoint — deliberately not probed live, since probing it would make a real change in the vendor’s system.',
}

export function classificationPlain(classification) {
  if (CLASSIFICATION_PLAIN[classification]) return CLASSIFICATION_PLAIN[classification]
  const m = /^http_(\d+)$/.exec(classification || '')
  if (m) return `Got an HTTP ${m[1]} response, a status this harness doesn't have a specific rule for — worth a human look.`
  return null
}
